/**
 * Install Base Service
 * 
 * Handles all API calls related to the install base feature
 */
import { apiService } from './api.service';
import { API_PATH } from '../config/api.config';
import axios from 'axios';

// API endpoints
const ENDPOINTS = {
  // Customer endpoints
  CUSTOMERS: '/install-base/customers',
  CUSTOMER_BY_ID: (id) => `/install-base/customers/${id}`,
  
  // Location endpoints
  LOCATIONS: '/install-base/locations',
  LOCATION_BY_ID: (id) => `/install-base/locations/${id}`,
  LOCATION_DETAILS: '/install-base/locations/details',
  
  // Equipment model endpoints
  EQUIPMENT_MODELS: '/install-base/equipment-models',
  EQUIPMENT_MODEL_BY_ID: (id) => `/install-base/equipment-models/${id}`,
  
  // Installation endpoints
  INSTALLATIONS: '/install-base/installations',
  INSTALLATION_BY_ID: (id) => `/install-base/installations/${id}`,
  INSTALLATIONS_NEEDING_SERVICE: '/install-base/installations/needing-service',
  INSTALLATIONS_BY_LOCATION: (locationId) => `/install-base/installations?location_id=${locationId}`,
  
  // Alert endpoints
  ALERTS: '/install-base/alerts',
  ALERTS_BY_INSTALLATION: (installationId) => `/install-base/installations/${installationId}/alerts`,
  ALERTS_BY_LOCATION: (locationId) => `/install-base/locations/${locationId}/alerts`,
  LOCATION_ALERT_STATS: (locationId) => `/install-base/locations/${locationId}/alert-stats`,

  // Inventory endpoints
  INVENTORY_ON_SITE: (locationId) => `/api/v1/inventory/location/${locationId}/onhand`
};

const API_URL = `${API_PATH}/install-base`;

/**
 * Install Base API functions
 */
export const installBaseService = {
  // Customer API functions
  getCustomers: () => apiService.get(ENDPOINTS.CUSTOMERS),
  getCustomerById: (id) => apiService.get(ENDPOINTS.CUSTOMER_BY_ID(id)),
  createCustomer: (data) => apiService.post(ENDPOINTS.CUSTOMERS, data),
  updateCustomer: (id, data) => apiService.put(ENDPOINTS.CUSTOMER_BY_ID(id), data),
  deleteCustomer: (id) => apiService.delete(ENDPOINTS.CUSTOMER_BY_ID(id)),
  
  // Location API functions
  getLocations: () => apiService.get(ENDPOINTS.LOCATIONS),
  getLocationById: (id) => apiService.get(ENDPOINTS.LOCATION_BY_ID(id)),
  getLocationDetails: async (locationId) => {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      const response = await apiService.get(`${ENDPOINTS.LOCATION_DETAILS}?location_id=${locationId}`);
      return response;
    } catch (error) {
      console.error('Error fetching location details:', error);
      throw error;
    }
  },
  getAllLocationDetails: () => apiService.get(ENDPOINTS.LOCATION_DETAILS),
  createLocation: (data) => apiService.post(ENDPOINTS.LOCATIONS, data),
  updateLocation: (id, data) => apiService.put(ENDPOINTS.LOCATION_BY_ID(id), data),
  deleteLocation: (id) => apiService.delete(ENDPOINTS.LOCATION_BY_ID(id)),
  
  // Equipment model API functions
  getEquipmentModels: () => apiService.get(ENDPOINTS.EQUIPMENT_MODELS),
  getEquipmentModelById: (id) => apiService.get(ENDPOINTS.EQUIPMENT_MODEL_BY_ID(id)),
  createEquipmentModel: (data) => apiService.post(ENDPOINTS.EQUIPMENT_MODELS, data),
  updateEquipmentModel: (id, data) => apiService.put(ENDPOINTS.EQUIPMENT_MODEL_BY_ID(id), data),
  deleteEquipmentModel: (id) => apiService.delete(ENDPOINTS.EQUIPMENT_MODEL_BY_ID(id)),
  
  // Installation API functions
  getInstallations: () => apiService.get(ENDPOINTS.INSTALLATIONS),
  getInstallationById: async (installationId) => {
    try {
      console.log('Fetching installation details for ID:', installationId);
      const response = await apiService.get(`/install-base/installations/${installationId}`);
      console.log('Installation API Response:', response);
      return response;
    } catch (error) {
      console.error('Error in getInstallationById:', error);
      throw error;
    }
  },
  createInstallation: (data) => apiService.post(ENDPOINTS.INSTALLATIONS, data),
  updateInstallation: (id, data) => apiService.put(ENDPOINTS.INSTALLATION_BY_ID(id), data),
  deleteInstallation: (id) => apiService.delete(ENDPOINTS.INSTALLATION_BY_ID(id)),
  getInstallationsNeedingService: () => apiService.get(ENDPOINTS.INSTALLATIONS_NEEDING_SERVICE),
  getInstallationsByLocation: (locationId) => apiService.get(ENDPOINTS.INSTALLATIONS_BY_LOCATION(locationId)),

  // Alert API functions
  getAlerts: () => apiService.get(ENDPOINTS.ALERTS),
  getAlertsByInstallation: async (installationId) => {
    try {
      if (!installationId) {
        throw new Error('Installation ID is required');
      }
      const response = await apiService.get(ENDPOINTS.ALERTS_BY_INSTALLATION(installationId));
      return response;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  },
  getAlertsByLocation: async (locationId) => {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      const response = await apiService.get(ENDPOINTS.ALERTS_BY_LOCATION(locationId));
      return response;
    } catch (error) {
      console.error('Error fetching location alerts:', error);
      throw error;
    }
  },
  getLocationAlertStats: async (locationId) => {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      const response = await apiService.get(ENDPOINTS.LOCATION_ALERT_STATS(locationId));
      return response;
    } catch (error) {
      console.error('Error fetching location alert stats:', error);
      throw error;
    }
  },
  createAlert: (data) => apiService.post(ENDPOINTS.ALERTS, data),
  updateAlert: (id, data) => apiService.put(`${ENDPOINTS.ALERTS}/${id}`, data),
  deleteAlert: (id) => apiService.delete(`${ENDPOINTS.ALERTS}/${id}`),

  // Inventory API functions
  getInventoryOnSite: (locationId) => apiService.get(ENDPOINTS.INVENTORY_ON_SITE(locationId)),

  getServiceRequests: async () => {
    try {
      const response = await apiService.get('/install-base/service-requests');
      return response;
    } catch (error) {
      console.error('Error fetching service requests:', error);
      throw error;
    }
  }
};

export default installBaseService; 